/* eslint-disable no-unused-expressions */
import React, { useMemo, useState } from "react";
import { TB, TableWrapper, Td, Th } from "./styles";
import {
  Box,
  Checkbox,
  IconButton,
  Link,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  TableBody,
  TableCell,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import {
  ArrowDownwardSharp,
  ArrowUpward,
  LocationOn,
  MoreVert,
} from "@mui/icons-material";
import { TableHeadContainer } from "../PrimarySetups/components/Styles";

const formatDate = (dateString) => {
  const date = new Date(dateString);
  const day = date.getUTCDate().toString().padStart(2, "0");
  const month = (date.getUTCMonth() + 1).toString().padStart(2, "0");
  const year = date.getUTCFullYear();
  return `${day}/${month}/${year}`;
};

const sortEvents = (events, isSortDescending) => {
  return [...events].sort((a, b) => {
    if (!a.startDate || !b.startDate) return 0;

    const dateA = new Date(a.startDate);
    const dateB = new Date(b.startDate);

    // Compare by date (ignore time)
    const dayA = dateA.toISOString().split("T")[0];
    const dayB = dateB.toISOString().split("T")[0];

    if (dayA < dayB) return -1;
    if (dayA > dayB) return 1;

    // Same date → sort by distance (toggle asc/desc)
    return isSortDescending ? b.distance - a.distance : a.distance - b.distance;
  });
};

const COLUMN_OPTIONS = ["City", "Category", "Address"];

const getEventDetailId = (event) => {
  if (!event) return undefined;
  return (
    event?.eventDetails?.id ||
    event?.eventsDetail?.[0]?.id ||
    event?.id ||
    event?.eventID
  );
};

const getAllEventIds = (event) => {
  const ids = [];

  // Add the main event detail ID
  const mainId = getEventDetailId(event);
  if (mainId) {
    ids.push(mainId);
  }

  // Add all IDs from eventsDetail array
  if (Array.isArray(event?.eventsDetail)) {
    event.eventsDetail.forEach((detail) => {
      if (detail?.id) {
        ids.push(detail.id);
      }
    });
  }

  return ids;
};

export default function EventDetailTable({
  eventDetails,
  count,
  page,
  handleChangePage,
  handleChangeRowsPerPage,
  rowsPerPage,
  selectedRows,
  setSelectedRows,
  permission,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [isSortDescending, setIsSortDescending] = useState(false);

  const events = useMemo(() => {
    const initialEvents = eventDetails?.event || [];
    return sortEvents(initialEvents, isSortDescending);
  }, [eventDetails, isSortDescending]);

  const selectableEvents = useMemo(
    () => events.filter((item) => !item.isSelected),
    [events],
  );
  const isAllSelected = useMemo(
    () =>
      selectableEvents.length > 0 &&
      selectedRows.length === selectableEvents.length,
    [selectableEvents.length, selectedRows.length],
  );

  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleToggle = (item) => {
    setSelectedColumns((prev) => {
      const currentIndex = prev.indexOf(item);
      if (currentIndex === -1) {
        return [...prev, item];
      } else {
        return prev.filter((col) => col !== item);
      }
    });
  };

  const handleChangeOrder = () => {
    setIsSortDescending((prev) => !prev);
  };

  const handleCheckboxChange = (event) => {
    const eventIds = getAllEventIds(event);
    if (eventIds.length === 0) return;

    setSelectedRows((prevSelectedRows) => {
      const mainId = getEventDetailId(event);
      const isSelected = prevSelectedRows.includes(mainId);

      if (isSelected) {
        return prevSelectedRows.filter((id) => !eventIds.includes(id));
      } else {
        return [...prevSelectedRows, ...eventIds];
      }
    });
  };

  const handleSelectAllChange = (event) => {
    if (event.target.checked) {
      const allEventIds = selectableEvents
        .flatMap((evt) => getAllEventIds(evt))
        .filter(Boolean);
      setSelectedRows(allEventIds);
    } else {
      setSelectedRows([]);
    }
  };

  return (
    <Box>
      <TableWrapper>
        <TB
          stickyHeader
          sx={{
            maxHeight: "60vh",
            borderRadius: "8px 8px 0px 0px",
          }}
        >
          <TableHeadContainer
            sx={{
              borderRadius: "8px 8px 0px 0px",
            }}
          >
            <TableRow>
              <Th
                style={{
                  borderRadius: "8px 0px 0px 0px",
                }}
              >
                Event Name
              </Th>
              {selectedColumns.includes("Category") && <Th>Category</Th>}
              <Th>Venue</Th>
              {selectedColumns.includes("Address") && <Th>Event Address</Th>}
              <Th>Event Date</Th>
              <Th>Number of Attendees</Th>
              {selectedColumns.includes("City") && <Th>City</Th>}

              <Th>
                Distance
                <IconButton onClick={handleChangeOrder} size="small">
                  {isSortDescending ? (
                    <ArrowUpward fontSize="small" />
                  ) : (
                    <ArrowDownwardSharp fontSize="small" />
                  )}
                </IconButton>
              </Th>
              <Th>
                <Stack
                  style={{
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "row",
                    borderRadius: "0px 8px 0px 0px",
                    padding:
                      selectableEvents.length === 0 ? "12px 0px" : "3px 0px",
                  }}
                >
                  <Stack
                    direction="row"
                    gap={1}
                    justifyContent="end"
                    alignItems="center"
                    flex={1}
                  >
                    <span>Select</span>
                    {selectableEvents.length > 0 && (
                      <Checkbox
                        size="small"
                        disabled={!permission}
                        checked={isAllSelected}
                        indeterminate={
                          selectedRows.length > 0 &&
                          selectedRows.length < selectableEvents.length
                        }
                        onChange={handleSelectAllChange}
                      />
                    )}
                  </Stack>
                  <Box
                    sx={{
                      width: "30%",
                      display: "flex",
                      justifyContent: "end",
                    }}
                  >
                    <IconButton onClick={handleClick} size="small">
                      <MoreVert />
                    </IconButton>
                  </Box>

                  <Menu
                    id="basic-menu"
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    MenuListProps={{
                      "aria-labelledby": "basic-button",
                    }}
                  >
                    {COLUMN_OPTIONS.map((item) => (
                      <MenuItem key={item} onClick={() => handleToggle(item)}>
                        <ListItemIcon>
                          <Checkbox
                            edge="start"
                            checked={selectedColumns.includes(item)}
                            tabIndex={-1}
                            disableRipple
                          />
                        </ListItemIcon>
                        <ListItemText primary={item} />
                      </MenuItem>
                    ))}
                  </Menu>
                </Stack>
              </Th>
            </TableRow>
          </TableHeadContainer>
          <TableBody>
            {events.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={12}
                  sx={{
                    textAlign: "center",
                    border: "0px",
                    height: "200px",
                  }}
                >
                  <Typography>No Events Found</Typography>
                </TableCell>
              </TableRow>
            )}
            {events.map((event) => (
              <TableRow key={getEventDetailId(event) ?? event?.name}>
                <Td
                  sx={{
                    maxWidth: "160px",
                    textAlign: "left !important",
                  }}
                >
                  {event?.name || "N/A"}
                </Td>
                {selectedColumns.includes("Category") && (
                  <Td
                    sx={{
                      maxWidth: "120px",
                    }}
                  >
                    {event?.category || "N/A"}
                  </Td>
                )}
                <Td
                  sx={{
                    maxWidth: "130px",
                  }}
                >
                  {event?.venue || "N/A"}
                </Td>
                {selectedColumns.includes("Address") && (
                  <Td
                    sx={{
                      maxWidth: "150px",
                      textAlign: "left",
                    }}
                  >
                    <Box
                      display="flex"
                      justifyContent={event.address ? "start" : "center"}
                      width="100%"
                      gap={1}
                    >
                      {event?.latitude !== "None" &&
                        event?.longitude !== "None" && (
                          <Link
                            href={`https://www.google.com/maps/search/?api=1&query=${event?.latitude}%2C${event.longitude}`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <LocationOn
                              sx={{
                                color: "#163A90",
                              }}
                            />
                          </Link>
                        )}
                      <Typography textAlign={event.address ? "left" : "center"}>
                        {event.address || "N/A"}
                      </Typography>
                    </Box>
                  </Td>
                )}

                <Td>
                  {event.isGrouped
                    ? `${formatDate(event.startDate)} - ${formatDate(
                        event.endDate,
                      )}`
                    : formatDate(event.startDate)}
                </Td>
                <Td
                  sx={{
                    maxWidth: "60px",
                  }}
                >
                  {event?.estimatedAttendees || "N/A"}
                </Td>
                {selectedColumns.includes("City") && (
                  <Td
                    sx={{
                      maxWidth: "80px",
                    }}
                  >
                    {event?.city || "N/A"}
                  </Td>
                )}

                <Td sx={{ maxWidth: "80px" }}>{`${
                  event?.distance || 0
                } KM`}</Td>

                <Td>
                  <Checkbox
                    size="small"
                    disabled={event.isSelected || !permission}
                    checked={
                      event.isSelected ||
                      selectedRows.includes(getEventDetailId(event))
                    }
                    onChange={() => handleCheckboxChange(event)}
                  />
                </Td>
              </TableRow>
            ))}
          </TableBody>
        </TB>
      </TableWrapper>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, { label: "All", value: -1 }]}
        component="div"
        count={count}
        rowsPerPage={rowsPerPage === "All" ? -1 : rowsPerPage}
        page={rowsPerPage === "All" ? 0 : page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={(event) => {
          const value = event.target.value;
          handleChangeRowsPerPage({
            target: { value: value === -1 ? "All" : value },
          });
        }}
      />
    </Box>
  );
}
